import React, { CSSProperties, JSX, useEffect, useRef, useState } from 'react'
import { ChatOverlayView } from 'components/ChatOverlayView'
import { EmoteWallView } from 'components/EmoteWallView'
import { useOverlayPreviewSeam } from 'components/overlayPreview/usePreviewSeam'
import { ChatOverlayPreviewConfig, EmoteWallPreviewConfig } from 'components/overlayPreview/previewConfig'
import { SAMPLE_TAG_SCALE_VAR, sampleTagFontPx, wallSampleTagBasePx } from 'components/overlayPreview/previewFeeds'

const WALL_TAG_MIN_SCREEN_PX = 12

export const ChatOverlayMockup = ({
  config,
  fillHost = true
}: {
  config: ChatOverlayPreviewConfig
  fillHost?: boolean
}): JSX.Element => {
  const seam = useOverlayPreviewSeam({ mode: 'mockup' })
  if (seam == null) return <div data-testid='chat-overlay-mockup' />
  return (
    <ChatOverlayView
      chatId=''
      token=''
      fillHost={fillHost}
      preview={seam}
      previewConfig={config}
    />
  )
}

export const EmoteWallMockup = ({
  config,
  designWidth = 1920,
  designHeight = 1080
}: {
  config: EmoteWallPreviewConfig
  designWidth?: number
  designHeight?: number
}): JSX.Element => {
  const seam = useOverlayPreviewSeam({ mode: 'mockup' })
  const outerRef = useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const node = outerRef.current
    if (node == null) return
    setWidth(node.clientWidth)
    const ro = new ResizeObserver(() => setWidth(node.clientWidth))
    ro.observe(node)
    return () => ro.disconnect()
  }, [])
  const scale = width > 0 && designWidth > 0 ? width / designWidth : 0
  const tagScreenPx = sampleTagFontPx(wallSampleTagBasePx(config.emoteSize)) * scale
  const tagScale = tagScreenPx > 0 ? Math.max(1, WALL_TAG_MIN_SCREEN_PX / tagScreenPx) : 1
  const stageStyle: CSSProperties & Record<typeof SAMPLE_TAG_SCALE_VAR, string> = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: designWidth,
    height: designHeight,
    transform: `scale(${scale})`,
    transformOrigin: 'top left',
    [SAMPLE_TAG_SCALE_VAR]: String(tagScale)
  }
  return (
    <div
      ref={outerRef}
      data-testid='emote-wall-mockup'
      style={{ position: 'relative', width: '100%', height: Math.round(designHeight * scale), overflow: 'hidden' }}
    >
      {seam != null && scale > 0 && (
        <div style={stageStyle}>
          <EmoteWallView emoteWallId='' token='' fillHost preview={seam} previewConfig={config} />
        </div>
      )}
    </div>
  )
}
